/** Shared saved views — the SAME custom views v1 shows in its sidebar
 * (rec_saved_views rows: data = ["Name", 0, {spec}]). v2 reads them (Phase 10
 * SELECT policy), applies v1's applyViewSpec filter semantics verbatim, and
 * writes new/deleted views through the door — so a view saved in either app
 * appears in both.
 *
 * Spec keys (live inventory 2026-07-17): statuses/sources (multi, ANY-of),
 * status/source (legacy single), pmin/pmax, smin/smax, beds/baths (minimums),
 * market ('On Market'/'Off Market'), dup, city (comma list, ANY substring of
 * address), state (substring), createdWithin (days) / createdBetween [from,to],
 * listedWithin (days via CDOM) / listedBetween, sort, circles (map radii —
 * NOT evaluated in v2 yet: needs geocoding; views carrying circles are shown
 * with a ° marker and the circle test passes-through). */
import { http } from "../../lib/http";
import { idbGet, idbSet } from "../../lib/idb";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";
import type { RecOp } from "../../lib/outbox";
import type { Lead } from "../../types";

export interface ViewSpec {
  status?: string;
  source?: string;
  statuses?: string[];
  sources?: string[];
  market?: string;
  dup?: string;
  pmin?: number;
  pmax?: number;
  smin?: number;
  smax?: number;
  beds?: number;
  baths?: number;
  city?: string;
  state?: string;
  circles?: unknown[];
  createdWithin?: number | null;
  createdBetween?: [string | null, string | null];
  listedWithin?: number | null;
  listedBetween?: [string | null, string | null];
  sort?: string;
  distressed?: boolean; // only-distressed leads (v2 Distressed toggle)
  jobTag?: string; //      scraper-job tag filter (v2 job-tag select)
}

export interface SharedView {
  name: string;
  spec: ViewSpec;
  hasCircles: boolean;
}

const CACHE = "shared_views_v1";

const numOr = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() ? Number(String(v).replace(/[^0-9.-]/g, "")) : NaN;
  return Number.isFinite(n) && n !== 0 ? n : null;
};
const strOr = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);
const strArr = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x.trim() !== "") : [];

export function narrowSharedView(row: Record<string, unknown>): SharedView | null {
  const d = row["data"];
  if (!Array.isArray(d) || d.length < 3) return null;
  const name = strOr(d[0]) ?? strOr(row["nat_key"]);
  const rawSpec = d[2];
  if (!name || rawSpec === null || typeof rawSpec !== "object" || Array.isArray(rawSpec)) return null;
  const o = rawSpec as Record<string, unknown>;
  const spec: ViewSpec = {};
  const st = strOr(o["status"]);
  if (st) spec.status = st;
  const so = strOr(o["source"]);
  if (so) spec.source = so;
  const sts = strArr(o["statuses"]);
  if (sts.length) spec.statuses = sts;
  const sos = strArr(o["sources"]);
  if (sos.length) spec.sources = sos;
  const mk = strOr(o["market"]);
  if (mk) spec.market = mk;
  const dp = strOr(o["dup"]);
  if (dp) spec.dup = dp;
  for (const k of ["pmin", "pmax", "smin", "smax", "beds", "baths"] as const) {
    const n = numOr(o[k]);
    if (n !== null) spec[k] = n;
  }
  const ci = strOr(o["city"]);
  if (ci) spec.city = ci;
  const sta = strOr(o["state"]);
  if (sta) spec.state = sta;
  if (Array.isArray(o["circles"]) && o["circles"].length) spec.circles = o["circles"];
  const cw = numOr(o["createdWithin"]);
  if (cw !== null || o["createdWithin"] === 0) spec.createdWithin = cw ?? 0;
  const lw = numOr(o["listedWithin"]);
  if (lw !== null || o["listedWithin"] === 0) spec.listedWithin = lw ?? 0;
  if (Array.isArray(o["createdBetween"])) spec.createdBetween = [strOr(o["createdBetween"][0]), strOr(o["createdBetween"][1])];
  if (Array.isArray(o["listedBetween"])) spec.listedBetween = [strOr(o["listedBetween"][0]), strOr(o["listedBetween"][1])];
  const srt = strOr(o["sort"]);
  if (srt) spec.sort = srt;
  if (o["distressed"] === true) spec.distressed = true;
  const jt = strOr(o["jobTag"]);
  if (jt) spec.jobTag = jt;
  return { name, spec, hasCircles: !!spec.circles?.length };
}

export async function cachedSharedViews(): Promise<SharedView[] | null> {
  const c = await idbGet<{ at: number; rows: SharedView[] }>(CACHE);
  return c && Array.isArray(c.rows) ? c.rows : null;
}

export async function fetchSharedViews(token: string, signal?: AbortSignal): Promise<SharedView[]> {
  const rows = await http<unknown[]>(
    `${SUPABASE_URL}/rest/v1/rec_saved_views?select=nat_key,data&deleted_at=is.null&order=updated_at.asc,id.asc&limit=200`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` }, signal, timeoutMs: 15000 },
  );
  const out: SharedView[] = [];
  for (const r of Array.isArray(rows) ? rows : []) {
    const v = narrowSharedView(r as Record<string, unknown>);
    if (v) out.push(v);
  }
  await idbSet(CACHE, { at: Date.now(), rows: out });
  return out;
}

/** v1's exact storage shape: ["Name", 0, spec], keyed by the name. */
export function opPutSharedView(name: string, spec: ViewSpec): RecOp {
  return { s: "saved_views", op: "put", k: name, d: [name, 0, spec] as unknown as Record<string, unknown>[] };
}

export function opDelSharedView(name: string): RecOp {
  return { s: "saved_views", op: "del", k: name };
}

// ---- v1 applyViewSpec, transcribed -----------------------------------------

/** day-serial of a lead's creation (v1 _viewDayOf ≈ start-of-day of _ts). */
const dayStart = (ms: number): number => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

export function applyViewSpec(
  lead: Lead,
  effStatus: string, // leadStageOf: qual||stage||'Needs Review' (from edits)
  dupCount: number,
  s: ViewSpec,
  now: number,
): boolean {
  if (s.status && effStatus !== s.status) return false;
  if (s.source && lead.source !== s.source) return false;
  if (s.statuses?.length && !s.statuses.some((x) => effStatus === x)) return false;
  if (s.sources?.length && !s.sources.some((x) => lead.source === x)) return false;
  if (s.market === "On Market" && lead.marketStatus !== "on") return false;
  if (s.market === "Off Market" && lead.marketStatus === "on") return false;
  if (s.dup === "Has duplicates" && !(dupCount > 1)) return false;
  if (s.dup === "No duplicates" && dupCount > 1) return false;
  const pr = lead.price ?? 0;
  if (s.pmin && pr < s.pmin) return false;
  if (s.pmax && pr > s.pmax) return false;
  const sq = lead.sqft ?? 0;
  if (s.smin && sq < s.smin) return false;
  if (s.smax && sq > s.smax) return false;
  if (s.beds && !((lead.beds ?? 0) >= s.beds)) return false;
  if (s.baths && !((lead.baths ?? 0) >= s.baths)) return false;
  if (s.city) {
    const cities = s.city.split(",").map((x) => x.trim().toLowerCase()).filter(Boolean);
    if (cities.length && !cities.some((c) => lead.address.toLowerCase().includes(c))) return false;
  }
  if (s.state && !lead.address.toLowerCase().includes(s.state.toLowerCase())) return false;
  if (s.distressed && !lead.distressed) return false;
  if (s.jobTag && (lead.jobTag ?? "") !== s.jobTag) return false;
  // circles: not evaluated in v2 (no client geocoding yet) — pass-through.
  if (s.createdWithin !== undefined && s.createdWithin !== null) {
    if (!lead.createdAt) return false;
    const t0 = dayStart(now);
    if (Math.round((t0 - dayStart(lead.createdAt)) / 86400000) > s.createdWithin) return false;
  }
  if (s.createdBetween) {
    if (!lead.createdAt) return false;
    const d0 = dayStart(lead.createdAt);
    const from = s.createdBetween[0] ? new Date(s.createdBetween[0] + "T00:00:00").getTime() : -8.64e15;
    const to = s.createdBetween[1] ? new Date(s.createdBetween[1] + "T23:59:59").getTime() : 8.64e15;
    if (d0 < from || d0 > to) return false;
  }
  if (s.listedBetween) {
    let lb: number | null = null;
    if (lead.cdom !== null) lb = dayStart(now) - lead.cdom * 86400000;
    if (lb === null) return false;
    const from = s.listedBetween[0] ? new Date(s.listedBetween[0] + "T00:00:00").getTime() : -8.64e15;
    const to = s.listedBetween[1] ? new Date(s.listedBetween[1] + "T23:59:59").getTime() : 8.64e15;
    if (lb < from || lb > to) return false;
  }
  if (s.listedWithin !== undefined && s.listedWithin !== null) {
    if (lead.cdom === null) return false;
    if (lead.cdom > s.listedWithin) return false;
  }
  return true;
}
